import { useState, type ReactElement } from 'react';
import { api, type LocationView, type ZoneView } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { EMPTY } from '../shared/lib/format';
import { toneVars } from '../shared/lib/status';
import { useAsync } from '../shared/lib/use-async';
import { DataTable, type Column } from '../shared/ui/DataTable';
import { Card, primaryButtonStyle } from '../shared/ui/primitives';
import { DriversTable } from './DriversTable';
import { LocationDrawer } from './LocationDrawer';
import { ZoneDrawer } from './ZoneDrawer';

/**
 * Справочники (разд. 12): список таблицей, правка — в drawer справа.
 *
 * Диспетчеру доступны только на чтение — матрица прав ТЗ разд. 2.1. Кнопок правки он
 * не видит вовсе: элемент, недоступный роли, не рендерится, а не блокируется —
 * спрятанная кнопка честнее неактивной. Строки у него не открываются.
 *
 * `external_code` у локаций стоит первой колонкой не для порядка: по нему CT адресует
 * точку отправления (FR-628), и именно его диспетчер сверяет при разборе.
 *
 * После сохранения список перечитывается с сервера, а не правится на месте: сервер
 * мог привести значения (точка, зоны), и таблица должна показывать то, что записано.
 */
export function DirectoriesPage({
  section,
  canEdit,
}: {
  readonly section: 'locations' | 'zones' | 'drivers';
  readonly canEdit: boolean;
}): ReactElement {
  if (section === 'locations') {
    return <LocationsSection canEdit={canEdit} />;
  }

  if (section === 'zones') {
    return <ZonesSection canEdit={canEdit} />;
  }

  return (
    <Page title={section} canEdit={canEdit} addLabel={null} onAdd={null} notice={null}>
      <DriversSection />
    </Page>
  );
}

function Page({
  title,
  canEdit,
  addLabel,
  onAdd,
  notice,
  children,
}: {
  readonly title: 'locations' | 'zones' | 'drivers';
  readonly canEdit: boolean;
  readonly addLabel: string | null;
  readonly onAdd: (() => void) | null;
  readonly notice: string | null;
  readonly children: ReactElement | ReactElement[];
}): ReactElement {
  const messages = useMessages();

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)', minWidth: 0 }}>
      <header style={headerStyle}>
        <h1>{messages.cabinet.nav[title]}</h1>
        {canEdit ? (
          addLabel === null || onAdd === null ? null : (
            <button type="button" onClick={onAdd} style={primaryButtonStyle}>
              {addLabel}
            </button>
          )
        ) : (
          <span style={{ color: 'var(--text-muted)' }}>
            {messages.cabinet.directories.readOnly}
          </span>
        )}
      </header>

      {notice === null ? null : (
        <div role="status">
          <Card tone="ok">{notice}</Card>
        </div>
      )}

      {children}
    </div>
  );
}

type Editing<T> = { readonly kind: 'create' } | { readonly kind: 'edit'; readonly row: T } | null;

function LocationsSection({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.directories;
  const locations = useAsync(() => api.locations(), []);
  const zones = useAsync(() => api.zones(), []);
  const [editing, setEditing] = useState<Editing<LocationView>>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const zoneNames = new Map((zones.data ?? []).map((zone) => [zone.id, zone.name]));

  const columns: readonly Column<LocationView>[] = [
    {
      key: 'code',
      title: text.externalCode,
      width: '140px',
      render: (row) => <span className="num">{row.external_code}</span>,
    },
    { key: 'name', title: text.name, render: (row) => row.name },
    {
      key: 'type',
      title: text.type,
      width: '120px',
      render: (row) => (text.locationTypes as Record<string, string>)[row.type] ?? row.type,
    },
    {
      key: 'address',
      title: text.address,
      render: (row) => row.address?.raw ?? EMPTY,
    },
    {
      key: 'zones',
      title: text.zones,
      width: '200px',
      render: (row) =>
        (row.zone_ids ?? []).length === 0
          ? EMPTY
          : (row.zone_ids ?? []).map((id) => zoneNames.get(id) ?? id).join(', '),
    },
    {
      key: 'active',
      title: messages.cabinet.drivers.status,
      width: '120px',
      render: (row) => <ActiveTag active={row.is_active} />,
    },
  ];

  return (
    <Page
      title="locations"
      canEdit={canEdit}
      addLabel={text.addLocation}
      onAdd={() => {
        setNotice(null);
        setEditing({ kind: 'create' });
      }}
      notice={notice}
    >
      <LoadError error={locations.error} />
      <DataTable
        rows={locations.data?.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        rail={(row) => (row.is_active ? 'neutral' : 'wait')}
        {...(canEdit
          ? {
              onRowClick: (row: LocationView) => {
                setNotice(null);
                setEditing({ kind: 'edit', row });
              },
            }
          : {})}
        selectedKey={editing?.kind === 'edit' ? editing.row.id : null}
        emptyText={
          locations.loading && locations.data === null
            ? messages.cabinet.common.loading
            : canEdit
              ? text.emptyLocationsCanCreate
              : messages.cabinet.common.empty
        }
      />

      <>
        {editing === null ? null : (
          <LocationDrawer
            location={editing.kind === 'edit' ? editing.row : undefined}
            zones={zones.data ?? []}
            onClose={() => setEditing(null)}
            onSaved={(saved, kind) => {
              const name = saved?.name ?? (editing.kind === 'edit' ? editing.row.name : '');

              setEditing(null);
              setNotice(`${text.notices[kind]}: ${name}`);
              locations.reload();
            }}
          />
        )}
      </>
    </Page>
  );
}

function ZonesSection({ canEdit }: { readonly canEdit: boolean }): ReactElement {
  const messages = useMessages();
  const text = messages.cabinet.directories;
  const zones = useAsync(() => api.zones(), []);
  // Точки нужны только форме зоны — диспетчер их здесь не запрашивает.
  const locations = useAsync(async () => (canEdit ? api.locations() : null), [canEdit]);
  const [editing, setEditing] = useState<Editing<ZoneView>>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const columns: readonly Column<ZoneView>[] = [
    { key: 'name', title: text.name, render: (row) => row.name },
    {
      key: 'mode',
      title: messages.cabinet.order.mode,
      width: '180px',
      render: (row) =>
        row.mode === 'express' || row.mode === 'distribution'
          ? messages.cabinet.modes[row.mode]
          : EMPTY,
    },
    {
      key: 'priority',
      title: text.priority,
      width: '110px',
      align: 'right',
      render: (row) => <span className="num">{row.priority ?? EMPTY}</span>,
    },
    {
      key: 'sla',
      title: text.sla,
      width: '120px',
      align: 'right',
      render: (row) => (
        <span className="num">
          {row.sla_minutes === null || row.sla_minutes === undefined
            ? text.slaDefault
            : `${row.sla_minutes} ${messages.common.minutesShort}`}
        </span>
      ),
    },
    {
      key: 'geometry',
      title: text.geometry,
      width: '140px',
      render: (row) =>
        row.geometry === null || row.geometry === undefined ? (
          <span style={{ color: 'var(--warn)' }}>{text.noGeometry}</span>
        ) : (
          text.hasGeometry
        ),
    },
    {
      key: 'active',
      title: messages.cabinet.drivers.status,
      width: '120px',
      render: (row) => <ActiveTag active={row.is_active !== false} />,
    },
  ];

  return (
    <Page
      title="zones"
      canEdit={canEdit}
      addLabel={text.addZone}
      onAdd={() => {
        setNotice(null);
        setEditing({ kind: 'create' });
      }}
      notice={notice}
    >
      <LoadError error={zones.error} />
      <DataTable
        rows={zones.data ?? []}
        columns={columns}
        rowKey={(row) => row.id}
        rail={(row) => (row.is_active === false ? 'wait' : 'neutral')}
        {...(canEdit
          ? {
              onRowClick: (row: ZoneView) => {
                setNotice(null);
                setEditing({ kind: 'edit', row });
              },
            }
          : {})}
        selectedKey={editing?.kind === 'edit' ? editing.row.id : null}
        emptyText={
          zones.loading && zones.data === null
            ? messages.cabinet.common.loading
            : canEdit
              ? text.emptyZonesCanCreate
              : messages.cabinet.common.empty
        }
      />

      <>
        {editing === null ? null : (
          <ZoneDrawer
            zone={editing.kind === 'edit' ? editing.row : undefined}
            zones={zones.data ?? []}
            locations={(locations.data?.data ?? []).filter((location) => location.is_active)}
            onClose={() => setEditing(null)}
            onSaved={(saved, kind) => {
              const name = saved?.name ?? (editing.kind === 'edit' ? editing.row.name : '');

              setEditing(null);
              setNotice(`${text.notices[kind]}: ${name}`);
              zones.reload();
            }}
          />
        )}
      </>
    </Page>
  );
}

function ActiveTag({ active }: { readonly active: boolean }): ReactElement {
  const messages = useMessages();
  const vars = toneVars(active ? 'ok' : 'neutral');

  return (
    <span style={{ ...tagStyle, color: vars.color, background: vars.background }}>
      {active
        ? messages.cabinet.directories.statusActive
        : messages.cabinet.directories.statusInactive}
    </span>
  );
}

function LoadError({ error }: { readonly error: { readonly code: string } | null }): ReactElement {
  const messages = useMessages();

  return error === null ? (
    <></>
  ) : (
    <div role="alert" style={{ color: 'var(--danger)' }}>
      {`${messages.cabinet.common.error}: ${error.code}`}
    </div>
  );
}

function DriversSection(): ReactElement {
  const drivers = useAsync(() => api.drivers(), []);

  return <DriversTable rows={drivers.data?.data ?? []} loading={drivers.loading} />;
}

const headerStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-3)',
  justifyContent: 'space-between',
  alignItems: 'center',
} as const;

const tagStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 'var(--tag-h)',
  padding: '0 var(--sp-2)',
  borderRadius: 'var(--r-xs)',
  fontSize: 'var(--fs-xs)',
  fontWeight: 'var(--fw-bold)',
  whiteSpace: 'nowrap',
} as const;
